import React from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  Body,
  Footer,
  FooterNextButton,
  FooterPreviousButton,
} from 'QuoteApp/components';
import { useFooter } from 'QuoteApp/utils/hooks';
import { VehicleADASFeaturesType } from 'QuoteApp/services/endpoints/GetDetails';
import { useAppContext } from 'QuoteApp/services/providers';
import { AdvancedFeatureVehicleCard } from './AdvancedFeatureVehicleCard';
import styles from './styles.module.css';

export const AdvancedDriverAssistance = () => {
  const { previousStep, nextStep } = useFooter(4);
  const {
    details,
    canNavigate,
    fetchRate,
    refreshDetails,
    api,
  } = useAppContext();

  const vehiclesWithADAS = Object.keys(details.vehicles).filter(
    vehicleId => details.vehicles[vehicleId].needsAdas
  );

  const initialData: VehicleADASFeaturesType = vehiclesWithADAS.reduce(
    (acc, vehicleId) => {
      return {
        vehicles: {
          ...acc.vehicles,
          [vehicleId]: {
            parkingAssistance: '',
            drivingAssistance: '',
            activeAssistance: '',
          },
        },
      };
    },
    { vehicles: {} }
  );

  const VALIDATION_SCHEMA = Yup.object().shape({
    vehicles: Yup.object(
      vehiclesWithADAS.reduce(
        (acc, k) => ({
          ...acc,
          [k]: Yup.object().shape({
            parkingAssistance: Yup.string().oneOf(['yes', 'no']),
            drivingAssistance: Yup.string().oneOf(['yes', 'no']),
            activeAssistance: Yup.string().oneOf(['yes', 'no']),
          }),
        }),
        {}
      )
    ),
  });

  return (
    <Formik
      enableReinitialize
      validationSchema={VALIDATION_SCHEMA}
      initialValues={initialData}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          const insurableItems = vehiclesWithADAS.map(vehicleId => {
            const ADASFormValues = values.vehicles[vehicleId];

            return {
              identifier: vehicleId,
              adas: [
                {
                  identifier: 'parkingAssistance',
                  equipped: ADASFormValues.parkingAssistance,
                  source: {
                    kind: 'userReported',
                  },
                },
                {
                  identifier: 'drivingAssistance',
                  equipped: ADASFormValues.drivingAssistance,
                  source: {
                    kind: 'userReported',
                  },
                },
                {
                  identifier: 'activeAssistance',
                  equipped: ADASFormValues.activeAssistance,
                  source: {
                    kind: 'userReported',
                  },
                },
              ],
            };
          }, {});

          await api.saveAppData({
            data: { insurableItems },
          });
          await fetchRate();
          await refreshDetails();
          nextStep.onClickSuccess();
        } catch {
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, handleSubmit }) => {
        return (
          <>
            <Body data-testid="advanced-driver-assistance">
              <div className={styles.headerSection}>
                <h1>Advanced Driver-Assistance Systems (ADAS)</h1>
                <div className={styles.subText}>
                  We weren't able to verify the ADAS features for these
                  vehicles. Please indicate which features are installed.
                </div>
              </div>
              <div className={styles.contentWrapper}>
                {vehiclesWithADAS.map(vehicleId => {
                  const vehicle = details.vehicles[vehicleId];
                  return (
                    <div data-testid="adas-vehicle" key={vehicleId}>
                      <AdvancedFeatureVehicleCard
                        vehicle={vehicle}
                        vehicleId={vehicleId}
                      />
                    </div>
                  );
                })}
              </div>
            </Body>
            <Footer
              nextButton={
                <FooterNextButton
                  onClick={() => handleSubmit()}
                  disabled={isSubmitting || !canNavigate}
                  isSubmitting={isSubmitting}
                >
                  Next: Purchase
                </FooterNextButton>
              }
              previousButton={
                <FooterPreviousButton onClick={previousStep.onClickSuccess}>
                  Back
                </FooterPreviousButton>
              }
            />
          </>
        );
      }}
    </Formik>
  );
};
